import { InboxOutlined } from '@ant-design/icons';
import { Alert, Divider, Typography, Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import { openNotification } from '../common/utils';
import useS3FileUpload, {
  UPLOAD_FAILED_PROGRESS,
} from '../hooks/useS3FileUpload';
import ModalComponent from './ModalComponent';

const { Dragger } = Upload;
const { Link, Text } = Typography;

const getAcceptedFormats = (accept) =>
  accept
    ?.split(',')
    ?.map((format) => format?.trim()?.toLowerCase())
    ?.filter(Boolean) || [];

const isAcceptedFile = (file, acceptedFormats) => {
  if (acceptedFormats?.length === 0) return true;
  const fileName = file?.name?.toLowerCase() || '';
  const fileType = file?.type?.toLowerCase() || '';
  return acceptedFormats?.some((format) => {
    if (format?.startsWith('.')) return fileName?.endsWith(format);
    if (format?.endsWith('/*')) {
      return fileType?.startsWith(format?.replace('/*', '/'));
    }
    return fileType === format;
  });
};

const UploadFileModal = ({
  isModalOpen,
  setIsModalOpen,
  title,
  okText = 'Upload',
  multiple = false,
  accept,
  hint,
  existingFileUrl,
  existingFileLabel = 'A file is already attached.',
  getSignedPutUrl,
  onUploaded,
}) => {
  const acceptedFormats = getAcceptedFormats(accept);
  const formatsLabel = acceptedFormats?.join(', ');
  const defaultHint =
    acceptedFormats?.length > 0
      ? `Allowed formats: ${formatsLabel}.${
          multiple ? '' : ' One file at a time.'
        }`
      : null;

  const [fileList, setFileList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { uploadFile, progress, resetProgress } = useS3FileUpload();

  useEffect(() => {
    if (!isModalOpen) {
      setFileList([]);
      resetProgress();
    }
  }, [isModalOpen]);

  const getFileStatus = (file) => {
    const filePercent = progress?.[file?.uid];
    if (filePercent === undefined) return file?.status;
    if (filePercent === UPLOAD_FAILED_PROGRESS) return 'error';
    if (filePercent === 100) return 'done';
    return 'uploading';
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    if (fileList?.length === 0) {
      openNotification('error', 'Please select a file to upload.');
      return;
    }
    setIsSubmitting(true);
    try {
      const uploadedFiles = [];
      // eslint-disable-next-line no-restricted-syntax
      for (const file of fileList) {
        // eslint-disable-next-line no-await-in-loop
        const signedData = await uploadFile(
          file?.originFileObj,
          getSignedPutUrl,
        );
        uploadedFiles?.push({
          uid: file?.uid,
          name: file?.name,
          fileName: signedData?.fileName,
        });
      }
      await onUploaded(multiple ? uploadedFiles : uploadedFiles?.[0]);
      setIsModalOpen(false);
    } catch (error) {
      if (error?.message) {
        openNotification('error', error?.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      open={isModalOpen}
      title={title}
      okText={okText}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={isSubmitting}
      okButtonProps={{ disabled: fileList?.length === 0 }}
      cancelButtonProps={{ disabled: isSubmitting }}
      closable={!isSubmitting}
      keyboard={false}
      destroyOnClose
    >
      <Divider />
      {existingFileUrl && (
        <Alert
          className="mb-16"
          type="info"
          showIcon
          message={
            <>
              {existingFileLabel}{' '}
              <Link href={existingFileUrl} target="_blank" rel="noreferrer">
                View
              </Link>
            </>
          }
          description="Uploading a new file will replace it."
        />
      )}
      <Dragger
        accept={accept}
        multiple={multiple}
        maxCount={multiple ? undefined : 1}
        beforeUpload={(file) => {
          if (!isAcceptedFile(file, acceptedFormats)) {
            openNotification(
              'error',
              `${file?.name} is not supported. Allowed formats: ${formatsLabel}.`,
            );
            return Upload?.LIST_IGNORE;
          }
          return false;
        }}
        disabled={isSubmitting}
        fileList={fileList?.map((file) => ({
          ...file,
          status: getFileStatus(file),
          percent: progress?.[file?.uid] ?? 0,
        }))}
        onChange={({ fileList: updatedFileList }) =>
          setFileList(multiple ? updatedFileList : updatedFileList?.slice(-1))
        }
        onRemove={() => !isSubmitting}
        showUploadList={{ showRemoveIcon: !isSubmitting }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag {multiple ? 'files' : 'a file'} here to upload
        </p>
        {(hint || defaultHint) && (
          <p className="ant-upload-hint">
            <Text type="secondary">{hint || defaultHint}</Text>
          </p>
        )}
      </Dragger>
    </ModalComponent>
  );
};

export default UploadFileModal;
